import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/** Items in order, split into { section, items } runs. A section repeated
 *  later in the list would open a second group, so AppShell keeps them
 *  together — the grouping is the caller's order, not a re-sort. */
function grouped(items) {
  const out = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.section === item.section) last.items.push(item);
    else out.push({ section: item.section, items: [item] });
  }
  return out;
}

// "More" overflow for the secondary nav destinations. Renders as a bottom sheet
// on mobile (variant="bottom") and a dropdown on desktop (variant="desktop").
// `items` are pre-filtered by role in AppShell. The button lights up when the
// current route is one of its destinations.
//
// Items carry a `section`, and the sections are the point: shift types belong
// under Shifts, not in a drawer marked Settings. A flat list makes you remember
// where something was filed; a grouped one lets you find it by thinking about
// what it is for.
export default function NavMore({ items, variant, icon }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const ref = useRef(null);

  const active = items.some(
    (i) => loc.pathname === i.to || loc.pathname.startsWith(i.to + '/'),
  );
  const lit = active || open;

  // Close on navigation.
  useEffect(() => setOpen(false), [loc.pathname]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (variant === 'bottom') {
    return (
      <div className="flex-1 flex" ref={ref}>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="More"
          title="More"
          aria-expanded={open}
          className={`flex-1 flex items-center justify-center py-3 ${
            lit ? 'text-[#f5f1e8]' : 'text-[#f5f1e8]/55'
          }`}
        >
          {icon}
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-30 bg-black/30" onClick={() => setOpen(false)} />
            <div className="fixed inset-x-0 bottom-14 z-40 bg-white rounded-t-2xl border-t border-ink-100 p-2 pb-3 shadow-lg">
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-ink-200" />
            <div className="max-h-[70vh] overflow-y-auto">
              {grouped(items).map((g) => (
                <div key={g.section || 'x'} className="mb-1 last:mb-0">
                  {g.section && (
                    <p className="px-4 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-ink-400">
                      {g.section}
                    </p>
                  )}
                  <ul>
                    {g.items.map((i) => (
                      <li key={i.to}>
                        <NavLink
                          to={i.to}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 rounded-lg text-sm ${
                              isActive ? 'bg-ink-100 text-ink-900 font-medium' : 'text-ink-700 hover:bg-ink-50'
                            }`
                          }
                        >
                          {i.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop dropdown
  return (
    <div className="relative flex" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="More"
        title="More"
        aria-expanded={open}
        className={`flex items-center justify-center px-6 py-3 ${
          lit
            ? 'text-[#f5f1e8] shadow-[inset_0_-2px_0_0_#f5f1e8]'
            : 'text-[#f5f1e8]/60 hover:text-[#f5f1e8]'
        }`}
      >
        {icon}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 min-w-52 max-h-[75vh] overflow-y-auto bg-white rounded-md border border-ink-100 shadow-lg py-1">
          {grouped(items).map((g) => (
            <div key={g.section || 'x'} className="py-0.5 border-b border-ink-100 last:border-0">
              {g.section && (
                <p className="px-4 pt-1.5 pb-0.5 text-[11px] font-medium uppercase tracking-wider text-ink-400">
                  {g.section}
                </p>
              )}
              {g.items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm ${
                      isActive ? 'bg-ink-50 text-ink-900 font-medium' : 'text-ink-700 hover:bg-ink-50'
                    }`
                  }
                >
                  {i.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
